/**
 * Authentication Hook
 * 
 * Custom hook for handling user authentication logic.
 * Manages user state, localStorage persistence, and provides
 * authentication methods for login/logout functionality.
 */

import { useState, useEffect } from 'react';
import type { User } from '../types';

/**
 * Return type for useAuth hook
 */
interface UseAuthReturn {
  user: User | null;           // Current user data
  isAuthenticated: boolean;    // Authentication status
  login: (userData: User) => void;  // Login function
  logout: () => void;          // Logout function
  isLoading: boolean;          // Loading state during auth check
}

/**
 * useAuth Hook
 * 
 * Provides authentication state and methods for the application.
 * Handles user data persistence using localStorage and manages
 * authentication status throughout the app lifecycle.
 * 
 * @returns Object containing auth state and methods
 */
export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for stored authentication data on app load
  useEffect(() => {
    /**
     * Check if user data exists in localStorage and restore session
     */
    const checkStoredAuth = () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser) as User;
          setUser(userData);
        }
      } catch (error) {
        console.error('Error loading stored user data:', error);
        // Clear corrupted data
        localStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    checkStoredAuth();
  }, []);

  /**
   * Login function
   * 
   * Authenticates user and stores their data for session persistence
   * 
   * @param userData - User information to store
   */
  const login = (userData: User) => {
    setUser(userData);
    // Store user data in localStorage for persistence across sessions
    localStorage.setItem('user', JSON.stringify(userData));
  };

  /**
   * Logout function
   * 
   * Clears user data and removes stored session information
   */
  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  return {
    user,
    isAuthenticated: !!user, // Convert user object to boolean
    login,
    logout,
    isLoading,
  };
};
